import React, { ReactElement, useMemo, useState } from 'react';
import { TFunction } from 'next-i18next';
import ExportConfirmModal from './ExportConfirmModal';
import { useTranslation } from 'utils/localization';
import Modal from 'components/modal/Modal';
import EditForm from 'components/editForm/EditForm';
import {
    ExportApiType,
    FieldTypeApiEnum,
    ValuesStringInterface,
} from 'submodules/api_middleware';
import {
    FilterSize,
    GroupEditFieldInterface,
    LabelSize,
    Option,
} from 'components/interfaces/GeneralInterface';
import { CoachService } from 'services/coach';
import { FormResponseData } from 'services/user.service';
import { getDiscipline } from 'utils/helpers/browser/discipline';
import { getLocationSearchQuery } from 'submodules/api_middleware/src/client';
import { ConfirmModalInterface } from 'utils/helpers/exportTable';
import { FileStateInterface } from 'components/interfaces/EditFormInterface';
import { ROW_BREAK } from 'components/editForm/utils/items';

interface Props {
    onHide: () => void;
}

const ExportCoachesModal = ({ onHide }: Props): ReactElement => {
    const { t } = useTranslation();
    const [showConfirm, setShowConfirm] = useState<ConfirmModalInterface>();

    const fields = useMemo<GroupEditFieldInterface[]>(
        (): GroupEditFieldInterface[] => getFieldWithLocalization(t),
        []
    );

    const onSubmitAction = (values: ValuesStringInterface) => {
        const exclude: string[] = [];
        if (values.no_address) {
            exclude.push('address');
        }
        if (values.no_efficiency) {
            exclude.push('license_effectiveness');
        }
        if (values.no_licence) {
            exclude.push('license_type');
        }
        if (values.no_ni) {
            exclude.push('national_identifier');
        }

        setShowConfirm({ type: 'loading' });
        const query = getLocationSearchQuery();
        return new CoachService().getList({
            discipline: getDiscipline(),
            output: values.output,
            exclude,
            ...query,
        });
    };

    const onSuccessAction = (
        _: ValuesStringInterface,
        __: FileStateInterface,
        response: FormResponseData
    ) =>
        setShowConfirm({
            type: 'success',
            response: response.data as ExportApiType,
        });

    return (
        <>
            {showConfirm && (
                <ExportConfirmModal
                    type={showConfirm.type}
                    onHide={() => {
                        onHide();
                        setShowConfirm(null);
                    }}
                    response={showConfirm.response}
                />
            )}
            <Modal
                show={true}
                onHide={onHide}
                showCross
                title={'CoachPage.export.settings'}
                centerTitle
            >
                <EditForm
                    fields={fields}
                    cancel={onHide}
                    withCancelChangesBtn
                    cancelButtonTextKey={'general.cancel'}
                    buttonTextKey={'general.generate'}
                    submit={onSubmitAction}
                    onSuccess={onSuccessAction}
                    centerButtons
                />
            </Modal>
        </>
    );
};

export default ExportCoachesModal;

enum ExportTypeEnum {
    coaches_list = 'coaches-list',
    coaches_license = 'coaches-license',
    coaches_region = 'coaches-region',
    coaches_license_list = 'coaches-license-list',
}

const getFieldWithLocalization = (t: TFunction): GroupEditFieldInterface[] => [
    {
        fields: [
            {
                name: 'output',
                label: 'CoachPage.export.type',
                value: ExportTypeEnum.coaches_list,
                type: FieldTypeApiEnum.SELECT,
                size: FilterSize.HALF,
                labelSize: LabelSize.TOP,
                values: Object.values(ExportTypeEnum).map(
                    (type): Option => ({
                        value: type,
                        label: t(`CoachPage.export.output.${type}`),
                    })
                ),
                onChange: (value) => ({ output: value }),
                noEmptyOption: true,
            },
            ROW_BREAK,
            {
                name: 'no_address',
                label: 'CoachPage.export.no_address',
                type: FieldTypeApiEnum.CHECKBOX,
                size: FilterSize.HALF,
                disabledIfSuccess: (state) =>
                    state.output !== ExportTypeEnum.coaches_list,
                displayIfSuccess: (state) =>
                    state.output === ExportTypeEnum.coaches_list,
            },
            {
                name: 'no_efficiency',
                label: 'CoachPage.export.no_efficiency',
                type: FieldTypeApiEnum.CHECKBOX,
                size: FilterSize.HALF,
                disabledIfSuccess: (state) =>
                    state.output !== ExportTypeEnum.coaches_list,
                displayIfSuccess: (state) =>
                    state.output === ExportTypeEnum.coaches_list,
            },
            {
                name: 'no_licence',
                label: 'CoachPage.export.no_licence',
                type: FieldTypeApiEnum.CHECKBOX,
                size: FilterSize.HALF,
                disabledIfSuccess: (state) =>
                    state.output !== ExportTypeEnum.coaches_list,
                displayIfSuccess: (state) =>
                    state.output === ExportTypeEnum.coaches_list,
            },
            {
                name: 'no_ni',
                label: 'CoachPage.export.no_rc',
                type: FieldTypeApiEnum.CHECKBOX,
                size: FilterSize.HALF,
                disabledIfSuccess: (state) =>
                    state.output !== ExportTypeEnum.coaches_list,
                displayIfSuccess: (state) =>
                    state.output === ExportTypeEnum.coaches_list,
            },
        ],
    },
];
